import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { MonetizationOn } from "@material-ui/icons";
import firebase from "../firebase";
import { palette } from "../theme";

const toInputDate = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const fromInputDate = (value, endOfDay) => {
  const [y, m, d] = value.split("-").map(Number);
  return endOfDay ? new Date(y, m - 1, d, 23, 59, 59) : new Date(y, m - 1, d);
};

export default function CalculatorForm() {
  const [from, setFrom] = useState(toInputDate(new Date()));
  const [to, setTo] = useState(toInputDate(new Date()));
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const db = firebase.firestore();
    const base = db.collection("Main").doc(global.config.uid);

    try {
      const sessionsSnap = await base
        .collection("Sessions")
        .where("Date", ">=", fromInputDate(from))
        .where("Date", "<=", fromInputDate(to, true))
        .get();
      const sessions = sessionsSnap.docs.map((doc) => ({
        location: doc.data().Location || "",
        sessionType: doc.data().sessionType,
      }));

      const locationsSnap = await base.collection("Locations").get();
      const results = [];
      locationsSnap.forEach((doc) => {
        const matching = sessions.filter(
          (s) => s.location.toLowerCase() === (doc.data().Location || "").toLowerCase()
        );
        let sessionCount = 0;
        let groupCount = 0;
        let regularSalary = 0;
        let groupSalary = 0;
        matching.forEach((s) => {
          if (s.sessionType === "GroupSession") {
            groupCount++;
            groupSalary += parseFloat(doc.data().GroupSalary) || 0;
          } else {
            sessionCount++;
            regularSalary += parseFloat(doc.data().SessionSalary) || 0;
          }
        });
        results.push({
          location: doc.data().Location,
          sessionCount,
          regularSalary,
          groupCount,
          groupSalary,
        });
      });
      setRows(results);
    } catch (error) {
      console.error("Calculator failed:", error);
      setRows([]);
    }
    setLoading(false);
  };

  const totals =
    rows &&
    rows.reduce(
      (acc, r) => ({
        sessions: acc.sessions + r.sessionCount,
        regular: acc.regular + r.regularSalary,
        groups: acc.groups + r.groupCount,
        group: acc.group + r.groupSalary,
      }),
      { sessions: 0, regular: 0, groups: 0, group: 0 }
    );

  return (
    <>
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                required
                fullWidth
                type="date"
                label="From"
                value={from}
                onChange={(e) => e.target.value && setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                required
                fullWidth
                type="date"
                label="To"
                value={to}
                onChange={(e) => e.target.value && setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                disabled={loading}
                startIcon={<MonetizationOn />}
                sx={{ flexShrink: 0, minWidth: 170 }}
              >
                {loading ? "Calculating..." : "Check Salary"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {rows && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 800, backgroundColor: palette.primarySoft } }}>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Sessions</TableCell>
                  <TableCell align="right">Session Pay</TableCell>
                  <TableCell align="right">Groups</TableCell>
                  <TableCell align="right">Group Pay</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: palette.textSecondary, fontWeight: 600 }}>
                        No sessions found in that date range
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {rows.map((r) => (
                      <TableRow key={r.location} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{r.location}</TableCell>
                        <TableCell align="right">{r.sessionCount}</TableCell>
                        <TableCell align="right">${r.regularSalary.toFixed(2)}</TableCell>
                        <TableCell align="right">{r.groupCount}</TableCell>
                        <TableCell align="right">${r.groupSalary.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ${(r.regularSalary + r.groupSalary).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{ "& td": { fontWeight: 800, borderTop: `2px solid ${palette.border}` } }}
                    >
                      <TableCell>Total</TableCell>
                      <TableCell align="right">{totals.sessions}</TableCell>
                      <TableCell align="right">${totals.regular.toFixed(2)}</TableCell>
                      <TableCell align="right">{totals.groups}</TableCell>
                      <TableCell align="right">${totals.group.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: palette.cta }}>
                        ${(totals.regular + totals.group).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </>
  );
}
